import React, { FC, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import classnames from 'classnames';
import { useAppSelector } from 'hooks';
import { BinIcon, EditIconBlue, ExpandImageIcon, VerticalListActionsIcon } from 'assets/icons';
import { CustomPopover } from 'components';
import { selectUserDetails } from 'containers/app/appSlice';
import { getInitials } from 'utils/commonUtils';
import CustomAvatar from 'components/avatar/CustomAvatar';

import { JobTimelineEvent, JobTimelineEventType } from '../jobs.model';
import { getRelativeTimeDisplay } from '../utils';

interface JobNotesTimelineProps {
  jobNotesTimeline: JobTimelineEvent[];
  limit?: number;
  onEditNoteClick: (event: JobTimelineEvent) => void;
  onDeleteNoteClick: (event: JobTimelineEvent) => void;
  hasImageViewer?: boolean;
  showPreview?: (index: number, event: JobTimelineEvent) => void;
  timelineId: string;
  currentActionsPopoverId: number;
  setCurrentActionsPopoverId: (noteId: number) => void;
  subTitleClassName?: string;
  titleClassName?: string;
  subTextClassName?: string;
  timelineClassName?: string;
}

const JobNotesTimeline: FC<JobNotesTimelineProps> = ({
  jobNotesTimeline,
  limit,
  onEditNoteClick,
  onDeleteNoteClick,
  hasImageViewer,
  showPreview,
  timelineId,
  currentActionsPopoverId,
  setCurrentActionsPopoverId,
  subTitleClassName,
  titleClassName,
  subTextClassName,
  timelineClassName
}) => {
  const currentUser = useAppSelector(selectUserDetails);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isNewItemAnimating, setIsNewItemAnimating] = useState(false);
  const [previousLatestNoteId, setPreviousLatestNoteId] = useState<number | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Refs to store timeout IDs for cleanup
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const scrollCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isListItemActionsVisible = (noteId: number) => {
    return noteId === currentActionsPopoverId;
  };

  const onCloseListItemActionsPopover = (noteId: number) => {
    if (currentActionsPopoverId === noteId) {
      setCurrentActionsPopoverId(null);
    }
  };

  const onToggleListItemActions = (noteId: number) => {
    if (noteId === currentActionsPopoverId) {
      setCurrentActionsPopoverId(null);
    } else {
      setCurrentActionsPopoverId(noteId);
    }
  };

  // Auto-scroll and animation for new items
  useEffect(() => {
    const currentLatestNoteId = jobNotesTimeline.length > 0 ? jobNotesTimeline[0]?.noteId : null;
    const isLatestNoteDifferent = currentLatestNoteId !== previousLatestNoteId && currentLatestNoteId !== null;

    // Skip animation on initial load
    if (!hasInitialized) {
      setPreviousLatestNoteId(currentLatestNoteId);
      setHasInitialized(true);
      return;
    }

    if (isLatestNoteDifferent) {
      // Clear any existing timeouts to prevent conflicts
      if (scrollCheckTimeoutRef.current) {
        clearTimeout(scrollCheckTimeoutRef.current);
        scrollCheckTimeoutRef.current = null;
      }
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }

      setIsNewItemAnimating(true);

      const container = scrollContainerRef.current;
      if (container) {
        if (container.scrollTop === 0) {
          // Already at top, just animate
          animationTimeoutRef.current = setTimeout(() => {
            setIsNewItemAnimating(false);
          }, 100);
        } else {
          // Need to scroll to top first
          container.scrollTo({ top: 0, behavior: 'smooth' });

          const checkScrollComplete = () => {
            if (container.scrollTop === 0) {
              // Scroll complete, stop animation after short delay
              animationTimeoutRef.current = setTimeout(() => {
                setIsNewItemAnimating(false);
              }, 100);
            } else {
              // Still scrolling, check again
              scrollCheckTimeoutRef.current = setTimeout(checkScrollComplete, 50);
            }
          };

          // Start checking scroll completion
          scrollCheckTimeoutRef.current = setTimeout(checkScrollComplete, 100);
        }
      } else {
        // No container, just animate
        animationTimeoutRef.current = setTimeout(() => {
          setIsNewItemAnimating(false);
        }, 100);
      }

      // Update this after setting up the animation
      setPreviousLatestNoteId(currentLatestNoteId);
    }

    return () => {
      if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
      if (scrollCheckTimeoutRef.current) clearTimeout(scrollCheckTimeoutRef.current);
    };
  }, [jobNotesTimeline.length, jobNotesTimeline[0]?.noteId, hasInitialized]);

  // eslint-disable-next-line react/no-multi-comp
  const ListActionsAnchor: React.FC<{ isPanelOpen: boolean }> = ({ isPanelOpen }) => {
    return (
      <div id="jobs-notes-timeline-more-options">
        <VerticalListActionsIcon
          height={16}
          width={4}
          className={twMerge(
            'mx-2 shrink-0 cursor-pointer',
            classnames({
              'fill-current text-secondary': isPanelOpen
            })
          )}
        />
      </div>
    );
  };

  // eslint-disable-next-line react/no-multi-comp
  const ListItemActionsMenu: FC<{ timelineEvent: JobTimelineEvent }> = ({ timelineEvent }) => {
    return (
      <div className="space-y-3 text-sbase font-semibold text-primaryText">
        <div
          id={`${timelineId}-3dot-editnote`}
          role="listbox"
          onClick={e => {
            e.stopPropagation();
            onEditNoteClick(timelineEvent);
          }}
          className="flex cursor-pointer flex-row items-center gap-2.5">
          <EditIconBlue height={16} width={16} />
          <div>Edit note</div>
        </div>
        <div
          id={`${timelineId}-3dot-delete-note`}
          role="listbox"
          onClick={() => onDeleteNoteClick(timelineEvent)}
          className="flex cursor-pointer flex-row items-center gap-2.5">
          <BinIcon />
          <div>Delete note</div>
        </div>
      </div>
    );
  };

  const showPopoverMenu = (jobTimelineEvent: JobTimelineEvent) => {
    return (
      (jobTimelineEvent.type === JobTimelineEventType.Note || jobTimelineEvent.type === JobTimelineEventType.JobNote) &&
      jobTimelineEvent.merchantId === currentUser.id
    );
  };

  const getMerchantInitials = (timelineEvent: JobTimelineEvent) => {
    const merchantName = timelineEvent.merchantName;
    return getInitials(merchantName);
  };

  return (
    <div
      ref={scrollContainerRef}
      className={`customNormalScroll flex grow flex-col overflow-y-auto p-4 pb-5 ${timelineClassName}`}>
      {jobNotesTimeline?.map(
        (jobTimelineEvent, index) =>
          (limit ? index < limit : true) && (
            <div
              key={`job-notes-timeline-${index + 1}`}
              className={twMerge(
                'flex gap-x-2.5',
                index === 0 && isNewItemAnimating && 'opacity-10',
                index === 0 &&
                  !isNewItemAnimating &&
                  hasInitialized &&
                  'opacity-100 transition-opacity duration-1000 ease-in',
                index === 0 && !hasInitialized && 'opacity-100'
              )}
              data-index={index}>
              <div className="flex flex-col items-center">
                <CustomAvatar
                  src={jobTimelineEvent?.merchantProfilePicture || ''}
                  height="h-6"
                  width="w-6"
                  className="text-[9px] font-semibold text-primaryText"
                  label={getMerchantInitials(jobTimelineEvent)}></CustomAvatar>
                {(limit && limit < jobNotesTimeline.length
                  ? index !== limit - 1
                  : index !== jobNotesTimeline.length - 1) && <div className="mb-2 h-full w-px bg-borderGray"></div>}
              </div>
              <div
                className={`w-full ${(limit ? index !== limit - 1 : index !== jobNotesTimeline.length - 1) && 'pb-6'}`}>
                <div className="flex w-full items-center justify-between">
                  <div className={`text-17px font-semibold text-primaryText ${titleClassName}`}>
                    {jobTimelineEvent?.merchantName}
                  </div>
                  {showPopoverMenu(jobTimelineEvent) && (
                    <CustomPopover
                      anchorComponentId="job-notes-anchor"
                      showArrow={true}
                      arrowOffset="right-3"
                      minWidth="min-w-[150px]"
                      highlightAnchor={false}
                      anchorComponent={ListActionsAnchor}
                      zIndex="z-[101]"
                      offset="-right-3"
                      margin="mt-1"
                      onClosePopover={() => onCloseListItemActionsPopover(jobTimelineEvent.noteId)}
                      show={isListItemActionsVisible(jobTimelineEvent.noteId)}
                      onTogglePopover={() => onToggleListItemActions(jobTimelineEvent.noteId)}>
                      {<ListItemActionsMenu timelineEvent={jobTimelineEvent} />}
                    </CustomPopover>
                  )}
                </div>
                {jobTimelineEvent.subTitle && (
                  <div
                    className={`mt-2.5 max-w-[260px] whitespace-pre-wrap break-words text-sbase font-medium 
                      text-primaryText ${subTitleClassName}`}>
                    {jobTimelineEvent.subTitle}
                  </div>
                )}
                {jobTimelineEvent.attachments?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-4">
                    {jobTimelineEvent.attachments.map((attachment, attachmentIndex) => (
                      <div
                        key={`job-event-${index}-attachment-${attachmentIndex}`}
                        className="relative h-[60px] max-h-[60px] w-[60px]">
                        <img
                          src={attachment.urlLink}
                          alt={`Attachment ${attachmentIndex + 1} for note`}
                          height={60}
                          width={60}
                          className="h-[60px] max-h-[60px] w-[60px] rounded object-cover"
                        />
                        {hasImageViewer && (
                          <ExpandImageIcon
                            className="absolute top-1 right-[3px] cursor-pointer"
                            onClick={() => showPreview(attachmentIndex, jobTimelineEvent)}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {jobTimelineEvent.timestamp && (
                  <div className={`mt-2 text-sbase text-accentText ${subTextClassName}`}>
                    {getRelativeTimeDisplay(jobTimelineEvent.timestamp)}
                  </div>
                )}
              </div>
            </div>
          )
      )}
    </div>
  );
};

export default JobNotesTimeline;
